// Drawer lateral derecho para reservar nueva clase.
// - Si licencia tiene cuota 'one': datepicker + slots libres del profe asignado.
// - Si licencia tiene cuota 'gr':  lista de grupales disponibles del profe asignado.
// - Si tiene ambas: tabs.

import { useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { Calendar } from '@/components/molecules/Calendar';
import { getTeacherFreeSlots, type FreeSlot } from '@/services/availability';
import { createOneBooking, joinGroupBooking, listGroupsAvailable, type BookingListItem } from '@/services/bookings';
import { ApiError } from '@/services/api';
import type { QuotaResponse } from '@/services/quota';
import styles from './BookingDrawer.module.scss';

interface BookingDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quota: QuotaResponse;
  onBooked: () => void;
}

type TabKey = 'one' | 'gr';

function isoDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatDayEs(d: Date): string {
  const s = d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function BookingDrawer({ open, onOpenChange, quota, onBooked }: BookingDrawerProps) {
  const teacherId = quota.assigned_teacher_id;
  const hasOne = (quota.license?.monthly_sessions_one ?? 0) > 0;
  const hasGr = (quota.license?.monthly_sessions_gr ?? 0) > 0;
  const remOne = quota.remaining.one;
  const remGr = quota.remaining.gr;

  const [tab, setTab] = useState<TabKey>(hasOne ? 'one' : 'gr');
  useEffect(() => {
    if (open) setTab(hasOne ? 'one' : 'gr');
  }, [open, hasOne]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.drawer} aria-describedby={undefined}>
          <header className={styles.header}>
            <Dialog.Title className={styles.title}>Reservar nueva clase</Dialog.Title>
            <Dialog.Close asChild>
              <button className={styles.closeBtn} aria-label="Cerrar">
                <Icon name="x" size={20} />
              </button>
            </Dialog.Close>
          </header>

          {!teacherId ? (
            <div className={styles.empty}>
              <p>No tienes profesor asignado. Contacta con el equipo de Knooowy para que te asignen uno.</p>
            </div>
          ) : !quota.license ? (
            <div className={styles.empty}>
              <p>No tienes una licencia activa. Contacta con el equipo de Knooowy.</p>
            </div>
          ) : (
            <>
              {hasOne && hasGr && (
                <div className={styles.tabs} role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'one'}
                    className={`${styles.tab} ${tab === 'one' ? styles.tabActive : ''}`}
                    onClick={() => setTab('one')}
                  >Individual ({remOne})</button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'gr'}
                    className={`${styles.tab} ${tab === 'gr' ? styles.tabActive : ''}`}
                    onClick={() => setTab('gr')}
                  >Grupal ({remGr})</button>
                </div>
              )}

              {tab === 'one' && hasOne && (
                <OneClassPicker
                  teacherId={teacherId}
                  remaining={remOne}
                  onBooked={onBooked}
                />
              )}
              {tab === 'gr' && hasGr && (
                <GroupClassList
                  remaining={remGr}
                  onJoined={onBooked}
                />
              )}
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface OneClassPickerProps {
  teacherId: number;
  remaining: number;
  onBooked: () => void;
}

function OneClassPicker({ teacherId, remaining, onBooked }: OneClassPickerProps) {
  const today = useMemo(() => new Date(), []);
  const [pickedDay, setPickedDay] = useState<string | null>(null);
  const [slots, setSlots] = useState<FreeSlot[]>([]);
  const [pickedSlot, setPickedSlot] = useState<FreeSlot | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!pickedDay) {
      setSlots([]);
      setPickedSlot(null);
      return;
    }
    let cancelled = false;
    setLoadingSlots(true);
    setError(null);
    const next = new Date(`${pickedDay}T00:00:00`);
    next.setDate(next.getDate() + 1);
    getTeacherFreeSlots(teacherId, pickedDay, isoDate(next))
      .then((r) => {
        if (cancelled) return;
        // Generamos sub-slots de 45 min cada hora con padding
        const expanded: FreeSlot[] = [];
        for (const w of r.slots) {
          const [sh, sm] = w.start.split(':').map(Number);
          const [eh, em] = w.end.split(':').map(Number);
          const startMin = sh! * 60 + sm!;
          const endMin = eh! * 60 + em!;
          for (let m = startMin; m + 45 <= endMin; m += 45) {
            const hh = String(Math.floor(m / 60)).padStart(2, '0');
            const mm = String(m % 60).padStart(2, '0');
            expanded.push({ date: w.date, start: `${hh}:${mm}`, end: '' });
          }
        }
        // Filtra slots en el pasado
        const nowMs = Date.now();
        const future = expanded.filter((s) => new Date(`${s.date}T${s.start}:00`).getTime() - nowMs >= 24 * 60 * 60 * 1000);
        setSlots(future);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Error al cargar disponibilidad'))
      .finally(() => !cancelled && setLoadingSlots(false));
    return () => { cancelled = true; };
  }, [pickedDay, teacherId]);

  async function confirm() {
    if (!pickedSlot) return;
    setSubmitting(true);
    setError(null);
    try {
      await createOneBooking(`${pickedSlot.date}T${pickedSlot.start}:00`);
      onBooked();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error al reservar';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (remaining === 0) {
    return (
      <div className={styles.empty}>
        <p>Has consumido todas tus clases individuales este mes.</p>
        <p className={styles.muted}>El día 1 del próximo mes se renueva tu cuota.</p>
      </div>
    );
  }

  return (
    <div className={styles.body}>
      <p className={styles.lead}>
        Tienes <strong>{remaining}</strong> {remaining === 1 ? 'clase individual' : 'clases individuales'} disponibles este mes.
        Reserva al menos con 24h de antelación.
      </p>

      <Calendar
        month={today}
        selectedDate={pickedDay}
        onDayClick={(d) => { setPickedDay(d); setPickedSlot(null); }}
        canGoPrev={false}
        canGoNext={false}
      />

      {pickedDay && (
        <section className={styles.slotsSection}>
          <h4 className={styles.slotsTitle}>{formatDayEs(new Date(`${pickedDay}T00:00:00`))}</h4>
          {loadingSlots ? (
            <div className={styles.center}><Spinner /></div>
          ) : slots.length === 0 ? (
            <p className={styles.muted}>Sin huecos disponibles ese día (o demasiado próximos: requieren 24h de antelación).</p>
          ) : (
            <div className={styles.slotsGrid}>
              {slots.map((s) => (
                <button
                  key={`${s.date}-${s.start}`}
                  type="button"
                  className={`${styles.slot} ${pickedSlot?.start === s.start ? styles.slotPicked : ''}`}
                  onClick={() => setPickedSlot(s)}
                >{s.start}</button>
              ))}
            </div>
          )}
        </section>
      )}

      {error && <p className={styles.error}>{error}</p>}

      <footer className={styles.footer}>
        <Button
          variant="primary"
          disabled={!pickedSlot || submitting}
          onClick={() => void confirm()}
        >
          {submitting ? 'Reservando…' : pickedSlot ? `Confirmar ${pickedDay} ${pickedSlot.start}` : 'Selecciona fecha y hora'}
        </Button>
      </footer>
    </div>
  );
}

interface GroupClassListProps {
  remaining: number;
  onJoined: () => void;
}

function GroupClassList({ remaining, onJoined }: GroupClassListProps) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<BookingListItem[]>([]);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listGroupsAvailable()
      .then((r) => !cancelled && setItems(r.items))
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : 'Error al cargar grupales'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  async function join(id: number) {
    setBusyId(id);
    setError(null);
    try {
      await joinGroupBooking(id);
      onJoined();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error al apuntarse';
      setError(msg);
    } finally {
      setBusyId(null);
    }
  }

  if (remaining === 0) {
    return (
      <div className={styles.empty}>
        <p>Has consumido todas tus clases grupales este mes.</p>
      </div>
    );
  }

  return (
    <div className={styles.body}>
      <p className={styles.lead}>
        Tienes <strong>{remaining}</strong> {remaining === 1 ? 'clase grupal' : 'clases grupales'} disponibles este mes. Apúntate a una de las programadas.
      </p>
      {loading && <div className={styles.center}><Spinner /></div>}
      {!loading && items.length === 0 && (
        <p className={styles.muted}>No hay clases grupales programadas con cupo libre este mes.</p>
      )}
      <ul className={styles.groupList}>
        {items.map((b) => {
          const dt = new Date(b.scheduled_at);
          const day = dt.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
          const time = `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
          const cupo = `${b.active_students}/${b.max_students}`;
          return (
            <li key={b.id} className={styles.groupItem}>
              <div>
                <div className={styles.groupHead}>{day} · {time}</div>
                <div className={styles.muted}>{b.duration_min} min · {cupo} alumnos · con {b.teacher_name}</div>
              </div>
              <Button variant="primary" size="sm" disabled={busyId === b.id} onClick={() => void join(b.id)}>
                {busyId === b.id ? 'Apuntando…' : 'Apuntarme'}
              </Button>
            </li>
          );
        })}
      </ul>
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}
